/**
 * PhotoCapture Component
 * Captures or selects photos with OCR plate detection and geo-tagging
 */

import { useState, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { readPlateWithPreprocess, type PlateOCRResult } from '../../lib/ocr/plate-reader'
import { extractPhotoMetadata, createThumbnail, compressPhoto, type PhotoMetadata } from '../../lib/photo/geo-tag'
import type { GeoLocation } from '../../lib/types'

export interface CapturedPhoto {
  id: string
  thumbnail: string
  blob: Blob
  location?: GeoLocation
  timestamp?: Date
  plateResult?: PlateOCRResult
}

interface PhotoCaptureProps {
  onPhotoCapture: (photo: CapturedPhoto) => void
  onPlateDetected?: (plate: string, confidence: number) => void
  currentLocation?: GeoLocation | null
  maxPhotos?: number
  disabled?: boolean
}

export function PhotoCapture({
  onPhotoCapture,
  onPlateDetected,
  currentLocation,
  maxPhotos = 3,
  disabled = false,
}: PhotoCaptureProps) {
  const { t } = useTranslation()
  const [isProcessing, setIsProcessing] = useState(false)
  const [progress, setProgress] = useState(0)
  const [photos, setPhotos] = useState<CapturedPhoto[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)

  const processImage = useCallback(async (file: File): Promise<CapturedPhoto | null> => {
    try {
      setIsProcessing(true)
      setProgress(0)

      // Extract EXIF metadata (GPS, timestamp)
      setProgress(10)
      const metadata: PhotoMetadata = await extractPhotoMetadata(file)

      // Use EXIF location or fallback to current device location
      const location = metadata.location || currentLocation || undefined

      // Create thumbnail for preview
      setProgress(20)
      const thumbnail = await createThumbnail(file, 200)

      // Compress photo for storage
      setProgress(30)
      const compressedBlob = await compressPhoto(file, 1200, 0.8)

      // Run OCR to detect license plate
      setProgress(40)
      const plateResult = await readPlateWithPreprocess(file, (p) => {
        setProgress(40 + p * 0.5) // 40-90% for OCR
      })

      setProgress(100)

      const photo: CapturedPhoto = {
        id: `photo-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        thumbnail,
        blob: compressedBlob,
        location,
        timestamp: metadata.timestamp || new Date(),
        plateResult: plateResult || undefined,
      }

      // Notify about detected plate
      if (plateResult && plateResult.confidence > 0.5 && onPlateDetected) {
        onPlateDetected(plateResult.text, plateResult.confidence)
      }

      return photo
    } catch (error) {
      console.error('[PhotoCapture] Error processing image:', error)
      return null
    } finally {
      setIsProcessing(false)
      setProgress(0)
    }
  }, [currentLocation, onPlateDetected])

  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    const remainingSlots = maxPhotos - photos.length
    const filesToProcess = Array.from(files).slice(0, remainingSlots)

    for (const file of filesToProcess) {
      if (!file.type.startsWith('image/')) continue

      const photo = await processImage(file)
      if (photo) {
        setPhotos(prev => [...prev, photo])
        onPhotoCapture(photo)
      }
    }

    // Reset input
    event.target.value = ''
  }, [photos.length, maxPhotos, processImage, onPhotoCapture])

  const handleCameraCapture = useCallback(() => {
    cameraInputRef.current?.click()
  }, [])

  const handleGallerySelect = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleRemovePhoto = useCallback((photoId: string) => {
    setPhotos(prev => prev.filter(p => p.id !== photoId))
  }, [])

  const canAddMore = photos.length < maxPhotos && !disabled

  return (
    <div className="space-y-3">
      {/* Photo Thumbnails */}
      {photos.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {photos.map((photo) => (
            <div
              key={photo.id}
              className="relative group"
            >
              <img
                src={photo.thumbnail}
                alt={t('vehicles.photo')}
                className="w-16 h-16 object-cover rounded-lg border border-white/20"
              />

              {/* Location indicator */}
              {photo.location && (
                <div className="absolute bottom-0.5 left-0.5 bg-black/60 rounded px-1">
                  <span className="text-[10px] text-green-400">📍</span>
                </div>
              )}

              {/* Plate detected indicator */}
              {photo.plateResult && photo.plateResult.confidence > 0.5 && (
                <div className="absolute bottom-0.5 right-0.5 bg-black/60 rounded px-1">
                  <span className="text-[10px] text-yellow-400">🚗</span>
                </div>
              )}

              {/* Remove button */}
              <button
                onClick={() => handleRemovePhoto(photo.id)}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500
                  rounded-full text-white text-xs flex items-center justify-center
                  opacity-0 group-hover:opacity-100 transition-opacity"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Processing Indicator */}
      {isProcessing && (
        <div className="bg-white/5 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-white/70">{t('vehicles.processingPhoto')}</span>
          </div>
          <div className="w-full bg-white/10 rounded-full h-1.5">
            <div
              className="bg-primary h-1.5 rounded-full transition-all duration-200"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="text-xs text-white/50 mt-1">
            {progress < 40 ? t('vehicles.extractingMetadata') :
             progress < 90 ? t('vehicles.readingPlate') :
             t('vehicles.finishing')}
          </div>
        </div>
      )}

      {/* Capture Buttons */}
      {canAddMore && !isProcessing && (
        <div className="flex gap-2">
          {/* Camera Button */}
          <button
            onClick={handleCameraCapture}
            disabled={disabled}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2
              bg-white/10 hover:bg-white/20 rounded-lg border border-white/10
              text-white/80 hover:text-white transition-colors
              disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span>📷</span>
            <span className="text-sm">{t('vehicles.takePhoto')}</span>
          </button>

          {/* Gallery Button */}
          <button
            onClick={handleGallerySelect}
            disabled={disabled}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2
              bg-white/10 hover:bg-white/20 rounded-lg border border-white/10
              text-white/80 hover:text-white transition-colors
              disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span>🖼️</span>
            <span className="text-sm">{t('vehicles.selectPhoto')}</span>
          </button>
        </div>
      )}

      {/* Photo count indicator */}
      {photos.length > 0 && (
        <div className="text-xs text-white/50 text-center">
          {t('vehicles.photoCount', { count: photos.length, max: maxPhotos })}
        </div>
      )}

      {/* Hidden file inputs */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  )
}

export default PhotoCapture
